/**
 * Wie Kan Wat Matrix (Epic C-2 — Kwalificaties, Story C-2.2, UX-C3)
 *
 * Rijen = medewerkers, kolommen = productcategorieën. Je kiest bovenaan de
 * activiteit, en bij een activiteit die een doelmarkt kent ook precies één
 * doelmarkt. De medewerkerslijst en het afdelingsfilter komen van de pagina.
 *
 * Tot 2026-08-03 was dit één rij per medewerker × categorie × doelmarkt. Bij
 * een ongefilterde activiteit liepen dat er honderden op, waarin je onmogelijk
 * kon lezen wie wat kan — terwijl dat de enige vraag is die deze weergave hoort
 * te beantwoorden.
 *
 * Dat de doelmarkt hier VERPLICHT is (geen "alle doelmarkten") is geen
 * beperking maar een voorwaarde: met categorieën als kolommen moet één vinkje
 * één ding betekenen. Zonder vaste markt zou het "gekwalificeerd in mínstens
 * één markt" zijn, en daar maakt een planner een verkeerde indeling op.
 *
 * Om diezelfde reden toont deze weergave NOOIT een raster terwijl de gegevens
 * nog onderweg zijn: alle vakjes staan dan uit, en "niemand is gekwalificeerd"
 * is precies de verkeerde conclusie.
 */
import { useMemo, useState } from 'react';
import {
  PLANNING_TASK_ACTIVITIES,
  PLANNING_TASK_ACTIVITY_LABELS,
  activityUsesTargetMarket,
  targetMarketKey,
} from '@functieloonhuis/shared';
import { useQualificationListItems } from '../hooks/useQualificationLists';
import { useWhoCanDoWhat } from '../hooks/useQualifications';
import { QualificationCheckbox } from './QualificationCheckbox';
import type { PlanningTaskActivity, QualificationScopedEmployeeDTO } from '@functieloonhuis/shared';

const SELECT_CLASS =
  'rounded-md border border-[#E2E8F0] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#54949E]';

interface WieKanWatMatrixProps {
  /** Al op afdeling gefilterd door de pagina. */
  employees: QualificationScopedEmployeeDTO[];
  isLoading: boolean;
}

export function WieKanWatMatrix({ employees, isLoading }: WieKanWatMatrixProps) {
  const [activity, setActivity] = useState<PlanningTaskActivity | ''>('');
  const [gekozenMarkt, setGekozenMarkt] = useState<number | null>(null);

  const { data: productCategories } = useQualificationListItems('product-categories');
  const { data: targetMarkets } = useQualificationListItems('target-markets');

  const heeftMarktAs = activity !== '' && activityUsesTargetMarket(activity);
  const markets = useMemo(() => targetMarkets ?? [], [targetMarkets]);
  const categories = productCategories ?? [];

  /**
   * De markt die dit scherm werkelijk toont.
   *
   * Afgeleid tijdens de render, niet via een `useEffect` die state zet. Dat
   * scheelt twee dingen: er is geen tussenrender waarin de oude markt nog bij
   * de nieuwe activiteit hoort (die verstuurde een overbodige aanvraag onder
   * een onmogelijke combinatie), en raakt de gekozen markt uit de lijst — hij
   * wordt gearchiveerd terwijl je kijkt — dan valt hij terug op de eerste in
   * plaats van blanco te blijven staan met een matrix die nog oude cijfers
   * toont.
   */
  const targetMarketId = useMemo(() => {
    if (!heeftMarktAs) return null;
    if (gekozenMarkt !== null && markets.some((m) => m.id === gekozenMarkt)) return gekozenMarkt;
    return markets[0]?.id ?? null;
  }, [heeftMarktAs, gekozenMarkt, markets]);

  const marktOntbreekt = heeftMarktAs && targetMarketId === null;

  const { data: rows, isLoading: rowsLoading } = useWhoCanDoWhat(
    activity === '' || marktOntbreekt ? null : { activity, targetMarketId: targetMarketId ?? undefined }
  );

  // Sleutel = medewerker:categorie. De doelmarkt zit al in de keuze hierboven;
  // de rij wordt er wél op gecontroleerd, via `targetMarketKey` zodat markt-loos
  // (`null`) en sleutel 0 hetzelfde zijn.
  const gekwalificeerd = useMemo(() => {
    const set = new Set<string>();
    for (const row of rows ?? []) {
      if (row.qualified && targetMarketKey(row.targetMarketId) === targetMarketKey(targetMarketId)) {
        set.add(`${row.employeeId}:${row.productCategoryId}`);
      }
    }
    return set;
  }, [rows, targetMarketId]);

  const marktNaam = markets.find((m) => m.id === targetMarketId)?.name ?? '';
  const aanHetLaden = isLoading || rowsLoading;

  function melding(testId: string, tekst: string) {
    return (
      <p data-testid={testId} className="py-12 text-center text-sm text-gray-500">
        {tekst}
      </p>
    );
  }

  // Als losse variabele, zodat TypeScript de activiteit binnen het matrixblok
  // versmald houdt zonder daar nog een `activity !== ''` te hoeven herhalen.
  const gekozenActiviteit: PlanningTaskActivity | null = activity === '' ? null : activity;
  const toonMatrix =
    gekozenActiviteit !== null &&
    !marktOntbreekt &&
    !aanHetLaden &&
    employees.length > 0 &&
    categories.length > 0;

  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-3">
        <select
          data-testid="wie-kan-wat-activiteit-select"
          aria-label="Activiteit"
          value={activity}
          onChange={(e) => setActivity(e.target.value as PlanningTaskActivity | '')}
          className={SELECT_CLASS}
        >
          <option value="">Kies een activiteit…</option>
          {PLANNING_TASK_ACTIVITIES.map((a) => (
            <option key={a} value={a}>
              {PLANNING_TASK_ACTIVITY_LABELS[a]}
            </option>
          ))}
        </select>

        {/* Geen "alle doelmarkten"-optie: zie de kop van dit bestand. */}
        {heeftMarktAs && markets.length > 0 && (
          <select
            data-testid="wie-kan-wat-doelmarkt-select"
            aria-label="Doelmarkt"
            value={targetMarketId ?? ''}
            onChange={(e) => setGekozenMarkt(e.target.value ? Number(e.target.value) : null)}
            className={SELECT_CLASS}
          >
            {markets.map((tm) => (
              <option key={tm.id} value={tm.id}>
                {tm.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {activity === '' && melding('wie-kan-wat-lege-staat', 'Kies een activiteit.')}

      {activity !== '' &&
        marktOntbreekt &&
        melding(
          'wie-kan-wat-geen-doelmarkten',
          'Deze activiteit werkt per doelmarkt, maar er zijn er nog geen. Beheer de doelmarktenlijst eerst.'
        )}

      {activity !== '' && !marktOntbreekt && aanHetLaden && melding('wie-kan-wat-laden', 'Bezig met laden…')}

      {activity !== '' &&
        !marktOntbreekt &&
        !aanHetLaden &&
        employees.length === 0 &&
        melding('wie-kan-wat-geen-medewerkers', 'Geen actieve medewerkers in deze afdeling.')}

      {activity !== '' &&
        !marktOntbreekt &&
        !aanHetLaden &&
        employees.length > 0 &&
        categories.length === 0 &&
        melding('wie-kan-wat-geen-categorieen', 'Nog geen productcategorieën — beheer de lijst eerst.')}

      {toonMatrix && gekozenActiviteit !== null && (
        <div className="overflow-x-auto">
          <table data-testid="wie-kan-wat-tabel" className="w-full border-collapse text-left text-sm">
            <caption className="sr-only">
              {PLANNING_TASK_ACTIVITY_LABELS[gekozenActiviteit]}
              {heeftMarktAs ? ` in ${marktNaam}` : ''} — wie kan welke productcategorie
            </caption>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="sticky left-0 z-10 border-b border-[#E2E8F0] bg-white py-1 pr-3 font-medium text-gray-500"
                >
                  Medewerker
                </th>
                {categories.map((pc) => (
                  <th
                    key={pc.id}
                    scope="col"
                    className="border-b border-[#E2E8F0] px-2 py-1 text-center align-bottom font-medium text-gray-500"
                  >
                    {pc.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {employees.map((emp) => (
                <tr key={emp.id} data-testid={`wie-kan-wat-rij-${emp.id}`}>
                  <th
                    scope="row"
                    className="sticky left-0 z-10 whitespace-nowrap border-b border-[#E2E8F0] bg-white py-1 pr-3 font-normal text-[#1E293B]"
                  >
                    {emp.name}
                  </th>
                  {categories.map((pc) => (
                    <td key={pc.id} className="border-b border-[#E2E8F0] px-2 py-1 text-center">
                      <QualificationCheckbox
                        employeeId={emp.id}
                        employeeName={emp.name}
                        activity={gekozenActiviteit}
                        productCategoryId={pc.id}
                        targetMarketId={targetMarketId}
                        qualified={gekwalificeerd.has(`${emp.id}:${pc.id}`)}
                        ariaLabel={[
                          `${emp.name}: ${PLANNING_TASK_ACTIVITY_LABELS[gekozenActiviteit]}`,
                          pc.name,
                          heeftMarktAs ? marktNaam : '',
                        ]
                          .filter(Boolean)
                          .join(', ')}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
